import { useState } from 'react';
import { OverlayedLoader, SmallContentContainer } from '@mews-commander/ui';
import { Form, lt } from '@mews/framework';
import {
    Card,
    Icon,
    IconName,
    SelectionCardGroup,
    Stack,
    StepDescription,
    ProgressIndicator,
    Typography,
} from '@optimus-web/core';
import { EnterpriseBuilderParametersType } from '@mews-commander/data-access';
import { useCopyEnterprise } from '../../queries/useCopyEnterprise';
import { EnterpriseBuilderProFormStep } from '../../types';
import { createEnterpriseBuilderParameters } from '../../utils';
import { EnterpriseBuilderProFormValues, useEnterpriseCopyFormProps } from './formMapping';
import { EnterpriseBuilderProFormFields } from './EnterpriseBuilderProFormFields';

const getSteps = (currentStep: EnterpriseBuilderProFormStep): StepDescription[] => [
    {
        editable: true,
        label: lt('ExportFrom'),
        value: EnterpriseBuilderProFormStep.ExportFrom,
        completed: currentStep !== EnterpriseBuilderProFormStep.ExportFrom,
    },
    {
        editable: currentStep === EnterpriseBuilderProFormStep.ExportSettings,
        label: lt('ImportTo'),
        value: EnterpriseBuilderProFormStep.ImportTo,
    },
    {
        editable: false,
        label: lt('ExportSettings'),
        value: EnterpriseBuilderProFormStep.ExportSettings,
    },
];

const createSelectionOptions = (propertyType: EnterpriseBuilderParametersType) => [
    {
        media: <Icon variant="primary" value={IconName.Property} size="medium" />,
        label: (
            <Typography textStyle="bodyMedium" as="div">
                {lt('ExistingProperty')}
            </Typography>
        ),
        value: EnterpriseBuilderParametersType.ExistingEnterprise,
        name: 'propertyType',
        checked: propertyType === EnterpriseBuilderParametersType.ExistingEnterprise,
        dataTestId: EnterpriseBuilderParametersType.ExistingEnterprise,
    },
    {
        media: <Icon variant="primary" value={IconName.Property} size="medium" />,
        label: (
            <Typography textStyle="bodyMedium" as="div">
                {lt('NewProperty')}
            </Typography>
        ),
        value: EnterpriseBuilderParametersType.NewEnterprise,
        name: 'propertyType',
        checked: propertyType === EnterpriseBuilderParametersType.NewEnterprise,
        dataTestId: EnterpriseBuilderParametersType.NewEnterprise,
    },
];

export const EnterpriseBuilderProFormWrapper = () => {
    const [propertyType, setPropertyType] = useState<EnterpriseBuilderParametersType>(
        EnterpriseBuilderParametersType.NewEnterprise
    );
    const [currentStep, setCurrentStep] = useState<EnterpriseBuilderProFormStep>(
        EnterpriseBuilderProFormStep.ExportFrom
    );

    const { mutate, isPending } = useCopyEnterprise();
    const formProps = useEnterpriseCopyFormProps(propertyType);

    const handleFormSubmit = (formData: EnterpriseBuilderProFormValues) => {
        mutate(createEnterpriseBuilderParameters(formData, propertyType));
    };

    return (
        <SmallContentContainer>
            <Stack vertical spacing="300">
                <ProgressIndicator
                    onStepChange={(step) => setCurrentStep(step as EnterpriseBuilderProFormStep)}
                    steps={getSteps(currentStep)}
                    activeStep={currentStep}
                />
                <Card padding="300">
                    <Stack vertical spacing="300">
                        {currentStep === EnterpriseBuilderProFormStep.ImportTo && (
                            <SelectionCardGroup
                                cardVariant="vertical"
                                type="radio"
                                onChange={(_e, value) => setPropertyType(value as EnterpriseBuilderParametersType)}
                                inputValue={propertyType}
                                options={createSelectionOptions(propertyType)}
                            />
                        )}
                        <Form onSubmit={handleFormSubmit} {...formProps}>
                            {({ submit }) => (
                                <>
                                    <OverlayedLoader show={isPending} />
                                    <EnterpriseBuilderProFormFields
                                        currentStep={currentStep}
                                        onStepChange={setCurrentStep}
                                        propertyType={propertyType}
                                        onSubmit={submit}
                                    />
                                </>
                            )}
                        </Form>
                    </Stack>
                </Card>
            </Stack>
        </SmallContentContainer>
    );
};
